import { authClient, posClient } from './client';
import {
  normalizeLoginResponse,
  normalizeMeResponse,
  wireLoginResponseSchema,
  wireMeResponseSchema,
  type LoginResult,
  type MeResult,
} from '@/types/auth';

/**
 * POST {auth}/api/pos/auth/login — the ONLY auth route
 * (auth/routes/api.php:3-7; AuthController.php:12-39).
 *
 * Errors (live-verified 2026-08-02):
 * - wrong credentials → 400 {"error":"Invalid credentials"} (NOT 401 — never
 *   triggers the logout path; authClient has no 401 interceptor)
 * - validation → 422 standard Laravel {"message","errors"}
 *
 * There is NO me/logout/refresh route on `auth`. Logout is purely client-side;
 * the session is re-CHECKED against pos-api instead (see `me` below).
 */
export const authApi = {
  async login(email: string, password: string): Promise<LoginResult> {
    const res = await authClient.post('/pos/auth/login', { email, password });
    return normalizeLoginResponse(wireLoginResponseSchema.parse(res.data));
  },

  /**
   * POST {pos}/auth/logout — revoke the token this call is made with.
   *
   * Behind `auth:api` on the server, so the token being ended is the token
   * presented. Login remains on the central auth issuer, while revocation is
   * handled by the consolidated authenticated POS surface.
   *
   * Answers 200 whether the token was live, already revoked or unknown — "this
   * session is over" is true in all three. The caller purges locally regardless
   * and never waits on this, so a failure here costs the user nothing beyond a
   * token that expires on its own schedule.
   */
  async logout(token: string): Promise<void> {
    await posClient.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
  },

  /**
   * GET {pos}/me — who the caller is and what they may do, ASKED AGAIN.
   *
   * pos-api, not `auth`: `auth` issues the token and knows nothing about
   * tenancy, while pos-api already resolves the organization (type included)
   * and the membership role on every single request. Sending X-Organization is
   * the whole point — the answer is scoped to the tenant currently selected.
   *
   * Failure is ORDINARY here. This runs at boot on a device that may be offline
   * or behind a sleeping API; the caller's contract is to keep the persisted
   * session exactly as it was and try again next boot. The one exception is a
   * 401, which posClient's own interceptor already turns into a purge, because
   * a dead token is not a stale field.
   */
  async me(options: { signal?: AbortSignal } = {}): Promise<MeResult> {
    const res = await posClient.get('/me', { signal: options.signal });
    return normalizeMeResponse(wireMeResponseSchema.parse(res.data));
  },
};
